import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Formik, Form, Field } from "formik";
import { Can } from '../../../rbac/Can';
import moment from "moment";
import { toast } from 'react-toastify';
// Fontawesome
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faAdd,
    faEdit,
    faTrash
  } from "@fortawesome/free-solid-svg-icons";
// Components
import Select from "../../../components/form/Select";
import TableLoader from "../../../components/TableLoader";
import Pagination from "../../../components/Pagination";
import ConfirmationModal from "../../../components/ConfirmationModal";
// Store and Services
import { getAccountUrls, deleteAccount, bulkDeleteAccounts } from "../../../services/accountService";
import { fetchAccounts, refresh, setFilters, setPage } from "../../../redux/slices/accountSlice";

function Accounts() {
    const { accounts, pagination, loading, filters, page } = useSelector((state) => state.account);
    const [urls, setUrls] = useState([]);
    const [selectedIds, setSelectedIds] = useState([]);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [deleteMode, setDeleteMode] = useState("single");
    const [accountToDelete, setAccountToDelete] = useState(null);
    const dispatch = useDispatch();

    const STATUS_OPTIONS = [
        { value: 0, label: "Inactive" },
        { value: 1, label: "Active" }
    ];


    useEffect(() => {
        getAccountUrls().then((res) => {
          setUrls(res.data.data);
        });
    }, []);

    useEffect(() => {
        dispatch(fetchAccounts());
    }, [dispatch, filters, page]);

    useEffect(() => {
        setSelectedIds([]);
    }, [accounts, page]);

    const handleSearch = (values) => {
        dispatch(setFilters(values));
    };

    const toggleSelectAll = () => {
        const pageIds = accounts?.map((item) => item.id) || [];

        if (pageIds.length === 0) {
            return;
        }

        const allSelected = pageIds.every((id) => selectedIds.includes(id));

        if (allSelected) {
            setSelectedIds((prev) => prev.filter((id) => !pageIds.includes(id)));
            return;
        }

        setSelectedIds((prev) => [...new Set([...prev, ...pageIds])]);
    };

    const toggleSelectOne = (id) => {
        setSelectedIds((prev) =>
            prev.includes(id) ? prev.filter((itemId) => itemId !== id) : [...prev, id]
        );
    };

    const handleDelete = (id) => {
        setDeleteMode("single");
        setAccountToDelete(id);
        setIsModalOpen(true);
    };

    const handleBulkDelete = () => {
        if (selectedIds.length === 0) {
            return;
        }

        setDeleteMode("bulk");
        setIsModalOpen(true);
    };

    const confirmDelete = async () => {
        try {
            const res = deleteMode === "bulk"
                ? await bulkDeleteAccounts(selectedIds)
                : await deleteAccount(accountToDelete);

            if (res.data.status === "success") {
                toast.success(res.data.message || "Account deleted successfully");
                setSelectedIds([]);
                dispatch(fetchAccounts());
            }

            setIsModalOpen(false);
        } catch (error) {
            toast.error(
                deleteMode === "bulk"
                    ? "Failed to delete selected accounts"
                    : "Failed to delete account"
            );
            setIsModalOpen(false);
        }
    };

    const cancelDelete = () => {
        setIsModalOpen(false);
    };

    const pageIds = accounts?.map((item) => item.id) || [];
    const allPageSelected = pageIds.length > 0 && pageIds.every((id) => selectedIds.includes(id));
    const somePageSelected = pageIds.some((id) => selectedIds.includes(id));
    
    return (
        <>
      <div className="flex flex-wrap items-end justify-between gap-4">
            <div>
              <p className="text-xs uppercase tracking-[0.22em] text-slate-400">Management</p>
              <h1>Accounts</h1>
            </div>
            <div className="flex gap-2">
                <Can I="delete" a="accounts">
                    {selectedIds.length > 0 && (
                        <button onClick={handleBulkDelete} className="btn-danger">
                            <FontAwesomeIcon icon={faTrash} className="mr-2"/>
                            Delete Selected ({selectedIds.length})
                        </button>
                    )}
                </Can>
                <Can I="create" a="accounts">
                    <Link to="/admin/accounts/add" className="btn-primary">
                        <FontAwesomeIcon icon={faAdd} className="mr-2"/>
                        Add Account
                    </Link>
                </Can>
            </div>
        </div>

        <hr />

        <Formik
            initialValues={filters}
            onSubmit={handleSearch}
        >
            {({ resetForm, submitForm }) => (
                <Form className="filter-form my-4">
                    <Select 
                        name="url" 
                        options={urls}
                        optionValue="value"
                        optionLabel="label"
                        placeholder="Select URL"
                    />
                    <Field
                        name="account"
                        type="text"
                        placeholder="Account"
                        className="form-control"
                    />
                    <Field
                        name="label"
                        type="text"
                        placeholder="Label"
                        className="form-control"
                    />
                    <Select
                        name="active_for_telegram"
                        options={STATUS_OPTIONS}
                        optionValue="value"
                        optionLabel="label"
                        placeholder="Select Pending Wagers"
                    />
                    <button type="submit" className="btn-primary">
                        Search
                    </button>
                    <button
                        type="button"
                        onClick={() => {
                            resetForm();
                            submitForm();
                        }}
                        className="btn-dark"
                    >
                        Reset
                    </button>
                </Form>
            )}
        </Formik>
        <hr />
        {loading ? (
            <TableLoader />    
        ) : (
            <>
            <table className="mt-6">
                <thead>
                    <tr>
                        <Can I="delete" a="accounts">
                            <th className="w-12">
                                <input
                                    type="checkbox"
                                    checked={allPageSelected}
                                    ref={(input) => {
                                        if (input) {
                                            input.indeterminate = somePageSelected && !allPageSelected;
                                        }
                                    }}
                                    onChange={toggleSelectAll}
                                    aria-label="Select all accounts on this page"
                                />
                            </th>
                        </Can>
                        <th>ID</th>
                        <th className="whitespace-nowrap">URL</th>
                        <th>Account</th>
                        <th>Password</th>
                        <th>Label</th>
                        <th>Pending Wagers</th>
                        <th>Last Alert</th>
                        <th>Action</th>
                    </tr>
                </thead>
                <tbody>
                    {accounts?.map((item, index) => (
                        <tr key={item?.id || index} className="text-center">
                            <Can I="delete" a="accounts">
                                <td className="overflow-hidden py-4">
                                    <input
                                        type="checkbox"
                                        checked={selectedIds.includes(item?.id)}
                                        onChange={() => toggleSelectOne(item?.id)}
                                        aria-label={`Select account ${item?.account}`}
                                    />
                                </td>
                            </Can>
                            <td className="overflow-hidden py-4">{item?.id}</td>
                            <td className="overflow-hidden py-4">{item?.url}</td>
                            <td className="overflow-hidden py-4">{item?.account}</td>
                            <td className="overflow-hidden py-4 font-mono text-xs">{item?.password}</td>
                            <td className="overflow-hidden py-4">{item?.label}</td>
                            <td className="overflow-hidden py-4">
                                <ActiveStatus
                                    value={item?.active_for_telegram}
                                    index={index}
                                />
                            </td>
                            <td className="overflow-hidden py-4">
                            {item?.last_alert_date ? moment(item?.last_alert_date).format("MM/DD/YYYY hh:mm A") : "No Alert"}
                            </td>
                            <td className="overflow-hidden py-4 flex gap-2 justify-center">
                            <Can I="update" a="accounts">
                                <Link to={`/admin/accounts/edit/${item?.id}`} className="btn-primary">
                                  <FontAwesomeIcon icon={faEdit} className="mr-2"/>
                                  Edit
                                </Link>
                            </Can>
                            <Can I="delete" a="accounts">
                            <button onClick={() => handleDelete(item?.id)} className="btn-danger">
                                <FontAwesomeIcon icon={faTrash} className="mr-2"/>
                                Delete
                            </button>
                            </Can>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
            <Pagination pagination={pagination} onPageChange={(page) => dispatch(setPage(page))} />
            </>
        )}

        <ConfirmationModal
            isOpen={isModalOpen}
            onClose={cancelDelete}
            onConfirm={confirmDelete}
            message={
                deleteMode === "bulk"
                    ? `Are you sure you want to delete ${selectedIds.length} selected account${selectedIds.length === 1 ? "" : "s"}?`
                    : "Are you sure you want to delete this account?"
            }
        />
        </>
    );
}

const ActiveStatus = (props) => (
    <span
        className={`status-badge ${
            props?.value ? "status-badge-active" : "status-badge-inactive"
        }`}
    >
        {props?.value ? "Active" : "Inactive"}
    </span>
);

export default Accounts;
